import { Badge } from '@/components/ui/badge';

/**
 * Shared display primitive for evaluation scores.
 *
 * Each portal wraps this with role-specific controls:
 * - Coach: adds evaluation button + notes editing
 * - Director: adds coach/parent assignment info + invite section
 * - Parent: read-only, no action buttons
 *
 * This is intentional — do NOT merge the portal wrappers into a single component
 * with conditional logic. The RBAC boundaries are clearer when each portal
 * composes its own page from shared primitives.
 */

interface EvaluationScoreEntry {
  id: string;
  attitude: number;
  effort: number;
  engagement: number;
  physical?: number | null;
  technical?: number | null;
  tactical?: number | null;
  competitionLevel: number;
  notes?: string | null;
  createdAt: string | number;
}

interface EvaluationScoresGridProps {
  evaluation: EvaluationScoreEntry;
  size?: 'sm' | 'md';
}

export function EvaluationScoresGrid({ evaluation, size = 'sm' }: EvaluationScoresGridProps) {
  const textSize = size === 'md' ? 'text-lg' : 'text-sm';
  const labelSize = size === 'md' ? 'text-xs' : 'text-[10px]';

  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
      <ScoreCell label="Attitude" value={evaluation.attitude} textSize={textSize} labelSize={labelSize} />
      <ScoreCell label="Effort" value={evaluation.effort} textSize={textSize} labelSize={labelSize} />
      <ScoreCell label="Engage" value={evaluation.engagement} textSize={textSize} labelSize={labelSize} />
      <ScoreCell label="Physical" value={evaluation.physical} textSize={textSize} labelSize={labelSize} />
      <ScoreCell label="Technical" value={evaluation.technical} textSize={textSize} labelSize={labelSize} />
      <ScoreCell label="Tactical" value={evaluation.tactical ?? evaluation.competitionLevel} textSize={textSize} labelSize={labelSize} />
    </div>
  );
}

function ScoreCell({
  label,
  value,
  textSize,
  labelSize,
}: {
  label: string;
  value: number | null | undefined;
  textSize: string;
  labelSize: string;
}) {
  return (
    <div>
      <p className={`${labelSize} text-muted-foreground`}>{label}</p>
      <p className={`${textSize} font-bold`}>{value ?? '-'}/4</p>
    </div>
  );
}

interface EvaluationCardProps {
  evaluation: EvaluationScoreEntry;
  size?: 'sm' | 'md';
}

export function EvaluationCard({ evaluation, size = 'sm' }: EvaluationCardProps) {
  return (
    <div className={size === 'md' ? 'p-4 border rounded-lg' : 'p-3 border rounded-lg'}>
      <div className="flex justify-between items-start mb-2">
        <EvaluationScoresGrid evaluation={evaluation} size={size} />
        <Badge variant="outline" className="text-xs ml-2 whitespace-nowrap">
          {new Date(evaluation.createdAt).toLocaleDateString()}
        </Badge>
      </div>
      {evaluation.notes && (
        <div className={size === 'md' ? 'pt-3 border-t' : 'pt-2 border-t'}>
          <p className="text-sm text-muted-foreground">{evaluation.notes}</p>
        </div>
      )}
    </div>
  );
}
